import usersService from '../../services/users'

const state = {
  users: [],
  currentUser: null,
  loading: false,
  error: null
}

const getters = {
  allUsers: state => state.users,
  userById: state => id => state.users.find(user => user.id === id),
  isLoading: state => state.loading,
  hasError: state => !!state.error,
  errorMessage: state => state.error
}

const mutations = {
  SET_USERS(state, users) {
    state.users = users
  },
  ADD_USER(state, user) {
    state.users.push(user)
  },
  UPDATE_USER(state, updatedUser) {
    const index = state.users.findIndex(user => user.id === updatedUser.id)
    if (index !== -1) {
      state.users.splice(index, 1, updatedUser)
    }
  },
  REMOVE_USER(state, userId) {
    state.users = state.users.filter(user => user.id !== userId)
  },
  SET_CURRENT_USER(state, user) {
    state.currentUser = user
  },
  SET_LOADING(state, isLoading) {
    state.loading = isLoading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  CLEAR_ERROR(state) {
    state.error = null
  }
}

const actions = {
  // Fetch all users
  async fetchUsers({ commit }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const response = await usersService.getUsers()
      commit('SET_USERS', response.data)
      return response
    } catch (error) {
      console.error('Error fetching users:', error)
      
      // Network errors will show a more specific error
      if (!error.response) {
        commit('SET_ERROR', `Network error: Unable to connect to server at ${usersService.getBaseUrl?.() || 'API server'}. Please check your connection.`)
      } else {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch users. Please try again later.')
      }
      
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  // Fetch a single user
  async fetchUser({ commit }, userId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const response = await usersService.getUser(userId)
      commit('SET_CURRENT_USER', response.data)
      return response
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch user')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  // Create a new user
  async createUser({ commit }, userData) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const response = await usersService.createUser(userData)
      commit('ADD_USER', response.data.user)
      return response
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to create user')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  // Update an existing user
  async updateUser({ commit }, { id, userData }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const response = await usersService.updateUser(id, userData)
      commit('UPDATE_USER', response.data.user)
      return response
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to update user')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  // Delete a user
  async deleteUser({ commit }, userId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const response = await usersService.deleteUser(userId)
      commit('REMOVE_USER', userId)
      return response
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to delete user')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  // Clear the current error
  clearError({ commit }) {
    commit('CLEAR_ERROR')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}